import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  CircularProgress,
  Box,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { NumericFormat } from "react-number-format";
import { toast } from "react-toastify";

import Navbar from "../../../globals/components/Navbar";
import TablePaginationActions from "../../../globals/components/TablePaginationActions";
import { apiClient } from "../../../api/httpClient";

const NumberFormatCustom = React.forwardRef(function NumberFormatCustom(props, ref) {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator
      isNumericString
      prefix="$"
      fixedDecimalScale
    />
  );
});

const DATE_PREFIX_RE = /^(\d{4}-\d{2}-\d{2})/;
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})/;

function normalizeDateInput(value) {
  if (!value) return "";
  const str = String(value);
  const match = DATE_PREFIX_RE.exec(str);
  return match ? match[1] : "";
}

function parseDateOnly(value) {
  if (!value) return null;
  const str = String(value);
  const match = DATE_ONLY_RE.exec(str);
  if (match) {
    const y = Number(match[1]);
    const mo = Number(match[2]) - 1;
    const d = Number(match[3]);
    return new Date(y, mo, d);
  }
  const dt = new Date(str);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function resolveDisplayDate(value, normalizeDates) {
  if (normalizeDates) {
    return parseDateOnly(value);
  }
  if (!value) {
    return null;
  }
  return new Date(value);
}

function formatDate(value, { normalizeDates }) {
  const d = resolveDisplayDate(value, normalizeDates);
  if (!d || Number.isNaN(d.getTime())) return normalizeDates ? "" : "N/A";
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  const year = d.getFullYear();
  return `${month}/${day}/${year}`;
}

function formatToUSDWithoutCents(amount) {
  if (!amount) return "$0";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(amount);
}

export default function EntityLimitsPageBase({
  title,
  entityIdParam,
  listPath,
  createPath,
  updatePath,
  deletePath,
  entityField,
  navigateBackOnDelete = false,
  normalizeDates = false,
  onAfterDelete,
}) {
  const params = useParams();
  const entityId = params[entityIdParam];

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [limits, setLimits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentLimitId, setCurrentLimitId] = useState(null);
  const [singleLimit, setSingleLimit] = useState(0);
  const [aggregateLimit, setAggregateLimit] = useState(0);
  const [effectiveDate, setEffectiveDate] = useState("");
  const [expirationDate, setExpirationDate] = useState("");

  const refreshLimits = async () => {
    const response = await apiClient(listPath(entityId), "GET");
    setLimits(response);
  };

  useEffect(() => {
    const fetchLimits = async () => {
      setLoading(true);
      try {
        await refreshLimits();
      } catch (error) {
        console.error("Failed to fetch limits:", error);
        toast.error("Failed to load limits. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchLimits();
  }, [entityId]);

  const currentRows = limits.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const openAddDialog = () => {
    setSingleLimit(0);
    setAggregateLimit(0);
    setEffectiveDate("");
    setExpirationDate("");
    setIsEditing(false);
    setOpen(true);
  };

  const openEditDialog = (limit) => {
    setSingleLimit(limit.line_of_business_single_limit);
    setAggregateLimit(limit.line_of_business_aggregate_limit);
    const effective = normalizeDates
      ? normalizeDateInput(limit.letter_of_authority_effective_date)
      : limit.letter_of_authority_effective_date;
    const expiration = normalizeDates
      ? normalizeDateInput(limit.letter_of_authority_expiration_date)
      : limit.letter_of_authority_expiration_date;
    setEffectiveDate(effective);
    setExpirationDate(expiration);
    setCurrentLimitId(limit.limit_id);
    setIsEditing(true);
    setOpen(true);
  };

  const handleSaveLimit = async () => {
    setLoading(true);
    try {
      const payload = {
        [entityField]: entityId,
        line_of_business_single_limit: singleLimit,
        line_of_business_aggregate_limit: aggregateLimit,
        letter_of_authority_effective_date: effectiveDate,
        letter_of_authority_expiration_date: expirationDate,
      };

      if (isEditing) {
        await apiClient(updatePath(currentLimitId), "PATCH", payload);
        toast.success("Limit updated successfully!");
      } else {
        await apiClient(createPath(entityId), "POST", payload);
        toast.success("Limit added successfully!");
      }

      setOpen(false);
      await refreshLimits();
    } catch (error) {
      console.error("Failed to save limit:", error);
      toast.error("Failed to save limit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLimit = async (limitId) => {
    setLoading(true);
    try {
      await apiClient(deletePath(limitId), "DELETE");
      toast.success("Limit deleted successfully!");
      await refreshLimits();
    } catch (error) {
      console.error("Failed to delete limit:", error);
      if (!navigateBackOnDelete) {
        toast.error("Failed to delete limit. Please try again.");
      }
    } finally {
      setLoading(false);
      if (navigateBackOnDelete) onAfterDelete?.();
    }
  };

  return (
    <>
      <Navbar />

      <Box sx={{ maxWidth: "100%", mt: 4, px: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
          {title}
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openAddDialog}>
            Add Limit
          </Button>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: "8px" }}>
            <Table>
              <TableHead>
                <TableRow style={{ backgroundColor: "#f0f0f0" }}>
                  <TableCell align="left">Effective Date</TableCell>
                  <TableCell align="left">Expiration Date</TableCell>
                  <TableCell align="right">Single Limit</TableCell>
                  <TableCell align="right">Aggregate Limit</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentRows.map((limit) => (
                  <TableRow key={limit.limit_id}>
                    <TableCell align="left">
                      {formatDate(limit.letter_of_authority_effective_date, { normalizeDates })}
                    </TableCell>
                    <TableCell align="left">
                      {formatDate(limit.letter_of_authority_expiration_date, { normalizeDates })}
                    </TableCell>
                    <TableCell align="right">
                      {formatToUSDWithoutCents(limit.line_of_business_single_limit)}
                    </TableCell>
                    <TableCell align="right">
                      {formatToUSDWithoutCents(limit.line_of_business_aggregate_limit)}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton color="primary" onClick={() => openEditDialog(limit)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteLimit(limit.limit_id);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          ActionsComponent={TablePaginationActions}
          component="div"
          count={limits.length}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number.parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{isEditing ? "Edit Limit" : "Add Limit"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Single Limit"
            fullWidth
            value={singleLimit.toString()}
            onChange={(e) => setSingleLimit(Number.parseFloat(e.target.value) || 0)}
            slotProps={{ input: { inputComponent: NumberFormatCustom } }}
          />
          <TextField
            margin="dense"
            label="Aggregate Limit"
            fullWidth
            value={aggregateLimit.toString()}
            onChange={(e) => setAggregateLimit(Number.parseFloat(e.target.value) || 0)}
            slotProps={{ input: { inputComponent: NumberFormatCustom } }}
          />
          <TextField
            margin="dense"
            label="Effective Date"
            type="date"
            fullWidth
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            margin="dense"
            label="Expiration Date"
            type="date"
            fullWidth
            value={expirationDate}
            onChange={(e) => setExpirationDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSaveLimit} color="primary" variant="contained">
            {isEditing ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

EntityLimitsPageBase.propTypes = {
  title: PropTypes.string.isRequired,
  entityIdParam: PropTypes.string.isRequired,
  listPath: PropTypes.func.isRequired,
  createPath: PropTypes.func.isRequired,
  updatePath: PropTypes.func.isRequired,
  deletePath: PropTypes.func.isRequired,
  entityField: PropTypes.string.isRequired,
  navigateBackOnDelete: PropTypes.bool,
  normalizeDates: PropTypes.bool,
  onAfterDelete: PropTypes.func,
};
